import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { IMPERSONATE_KEY, IMPERSONATE_EVENT } from "@/contexts/AppContext";
import { arDisplayStatus } from "@/lib/assetStatus";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface Manufacturer {
  id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  website: string | null;
  created_at: string;
  last_asset_request_sent_at: string | null;
  is_demo?: boolean;
  product_count?: number;
}

interface ManufacturerProduct {
  id: string;
  name: string;
  category: string | null;
  asset_status: string | null;
  active: boolean;
}

interface MfrUser {
  user_id: string;
  role: string;
  created_at: string;
}

type SortKey = "name" | "product_count" | "created_at";
type SortDir = "asc" | "desc";

interface FormState {
  name: string;
  contact_name: string;
  contact_email: string;
  website: string;
}

const BLANK_FORM: FormState = {
  name: "", contact_name: "", contact_email: "", website: "",
};

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const CATEGORY_LABELS: Record<string, string> = {
  gas_fireplace: "Gas Fireplace", wood_fireplace: "Wood Fireplace",
  pellet_stove: "Pellet Stove", electric_fireplace: "Electric Fireplace",
  fireplace_insert: "Fireplace Insert", gas_logs: "Gas Logs",
  chimney_venting: "Chimney & Venting", fireplace_accessory: "Hearth Accessory",
  patio_furniture: "Patio Furniture", outdoor_kitchen: "Outdoor Kitchen",
  pergola_gazebo: "Pergola / Gazebo", fire_pit: "Fire Pit",
  patio_heater: "Patio Heater", outdoor_accessory: "Outdoor Accessory",
  gas_grill: "Gas Grill", charcoal_grill: "Charcoal Grill",
  pellet_grill: "Pellet Grill", kamado_grill: "Kamado Grill",
  built_in_grill: "Built-in Grill", bbq_accessory: "BBQ Accessory",
};

// Keyed on ArDisplayStatus, not raw asset_status: the approved path writes
// `ready`, so a `complete` key alone left every published product grey.
const AR_DOT: Record<string, string> = {
  ready: "bg-green-500",
  processing: "bg-blue-500 animate-pulse",
  pending: "bg-yellow-500",
  failed: "bg-red-500",
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const inputCls =
  "w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 outline-none focus:border-orange-500 transition";

/* ------------------------------------------------------------------ */
/* Status badge                                                         */
/* ------------------------------------------------------------------ */

type SubTier = "pending" | "free" | "paid" | "premium";

const TIER_CLS: Record<SubTier, string> = {
  pending: "border-gray-700/60 bg-gray-900/30 text-gray-400",
  free:    "border-gray-700/60 bg-gray-900/30 text-gray-400",
  paid:    "border-teal-700/60 bg-teal-900/30 text-teal-400",
  premium: "border-amber-700/60 bg-amber-900/30 text-amber-400",
};

function MfrStatusBadge({ tier }: { tier: SubTier }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${TIER_CLS[tier]}`}>
      {tier}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Add / Edit Drawer                                                    */
/* ------------------------------------------------------------------ */

function ManufacturerDrawer({
  initial,
  onSave,
  onClose,
  saving,
}: {
  initial: FormState;
  onSave: (f: FormState) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const drawerRef = useRef<HTMLDivElement>(null);
  const isEdit = initial.name !== "" || initial.contact_email !== "";

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div ref={drawerRef} className="w-full max-w-md bg-gray-900 border-l border-gray-800 flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-800 shrink-0">
          <h2 className="font-semibold text-gray-100">
            {isEdit ? "Edit Manufacturer" : "Add Manufacturer"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-300">
              Company name <span className="text-orange-500">*</span>
            </label>
            <input
              className={inputCls}
              placeholder="e.g. Napoleon Grills"
              value={form.name}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-300">Contact name</label>
            <input
              className={inputCls}
              placeholder="e.g. Jane Smith"
              value={form.contact_name}
              onChange={(e) => patch({ contact_name: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-300">Contact email</label>
            <input
              className={inputCls}
              type="email"
              placeholder="contact@manufacturer.com"
              value={form.contact_email}
              onChange={(e) => patch({ contact_email: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-300">Website</label>
            <input
              className={inputCls}
              type="url"
              placeholder="https://manufacturer.com"
              value={form.website}
              onChange={(e) => patch({ website: e.target.value })}
            />
          </div>
        </div>

        <div className="p-5 border-t border-gray-800 shrink-0">
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name.trim()}
            className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Manufacturer"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Expanded product list                                                */
/* ------------------------------------------------------------------ */

function ManufacturerProducts({
  manufacturerId,
  jwt,
  navigate,
}: {
  manufacturerId: string;
  jwt: string;
  navigate: (to: string) => void;
}) {
  const [products, setProducts] = useState<ManufacturerProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await fetch(
        `${SB_URL}/rest/v1/products?manufacturer_id=eq.${manufacturerId}&select=id,name,category,asset_status,active&order=name.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (res.ok) setProducts(await res.json());
      setLoading(false);
    })();
  }, [manufacturerId, jwt]);

  if (loading) {
    return <div className="py-3 px-4 text-xs text-gray-500 animate-pulse">Loading products…</div>;
  }
  if (products.length === 0) {
    return <div className="py-3 px-4 text-xs text-gray-600">No products yet.</div>;
  }

  return (
    <div className="px-4 pb-4">
      <div className="rounded-lg border border-gray-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-900/80">
            <tr className="border-b border-gray-800">
              <th className="px-3 py-2 text-left text-gray-500 font-medium">Name</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium">Category</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium">AR</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-gray-800 hover:bg-gray-900/40 transition">
                <td className="px-3 py-2 text-gray-200">{p.name}</td>
                <td className="px-3 py-2 text-gray-400">
                  {CATEGORY_LABELS[p.category ?? ""] ?? p.category ?? "—"}
                </td>
                <td className="px-3 py-2">
                  {arDisplayStatus(p) && (
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${AR_DOT[arDisplayStatus(p)!] ?? "bg-gray-600"}`}
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs ${p.active ? "text-green-400" : "text-gray-600"}`}>
                    {p.active ? "Active" : "Draft"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => navigate(`/admin/products/${p.id}`)}
                    className="text-orange-400 hover:underline text-xs"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Manufacturer Users List (expanded row)                               */
/* ------------------------------------------------------------------ */

function ManufacturerUsersList({
  manufacturerId,
  jwt,
  onRemove,
}: {
  manufacturerId: string;
  jwt: string;
  onRemove: (userId: string) => void;
}) {
  const [users, setUsers] = useState<MfrUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await fetch(
        `${SB_URL}/rest/v1/manufacturer_users?manufacturer_id=eq.${manufacturerId}&select=user_id,role,created_at&order=created_at.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (res.ok) setUsers(await res.json());
      setLoading(false);
    })();
  }, [manufacturerId, jwt]);

  if (loading) return <div className="px-4 py-2 text-xs text-gray-500 animate-pulse">Loading users…</div>;
  if (users.length === 0) return <div className="px-4 py-2 text-xs text-gray-600">No users linked yet.</div>;

  return (
    <div className="px-4 pt-3 pb-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Users</p>
      <div className="rounded-lg border border-gray-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-900/80">
            <tr className="border-b border-gray-800">
              <th className="px-3 py-2 text-left text-gray-500 font-medium">User ID</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium">Role</th>
              <th className="px-3 py-2 text-left text-gray-500 font-medium">Added</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-gray-800 hover:bg-gray-900/40 transition">
                <td className="px-3 py-2 font-mono text-gray-400">{u.user_id.slice(0, 8)}…</td>
                <td className="px-3 py-2">
                  <span className={`capitalize font-medium ${u.role === "admin" ? "text-orange-400" : "text-gray-400"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-500">
                  {new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onRemove(u.user_id)}
                    className="text-red-500 hover:text-red-400 transition text-xs"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminManufacturers() {
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const jwt = session?.access_token ?? "";

  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subMap, setSubMap] = useState<Map<string, string>>(new Map());

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [requestingAssets, setRequestingAssets] = useState<Set<string>>(new Set());
  const [resettingDemoId, setResettingDemoId] = useState<string | null>(null);
  const [userCountMap, setUserCountMap] = useState<Map<string, number>>(new Map());

  // Invite modal
  const [inviteModalMfr, setInviteModalMfr] = useState<Manufacturer | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"viewer" | "admin">("viewer");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  /* ── Fetch ──────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!jwt) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch manufacturers
      const mRes = await fetch(
        `${SB_URL}/rest/v1/manufacturers?select=id,name,contact_name,contact_email,website,created_at,last_asset_request_sent_at,is_demo&order=name.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (!mRes.ok) throw new Error(`${mRes.status}`);
      const mfrs: Manufacturer[] = await mRes.json();

      // Fetch product counts per manufacturer
      const cRes = await fetch(
        `${SB_URL}/rest/v1/products?select=manufacturer_id`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      const products: { manufacturer_id: string | null }[] = cRes.ok ? await cRes.json() : [];
      const countMap: Record<string, number> = {};
      for (const p of products) {
        if (p.manufacturer_id) countMap[p.manufacturer_id] = (countMap[p.manufacturer_id] ?? 0) + 1;
      }

      setManufacturers(mfrs.map((m) => ({ ...m, product_count: countMap[m.id] ?? 0 })));

      // Fetch subscription tiers for status badges
      try {
        const sRes = await fetch(
          `${SB_URL}/rest/v1/manufacturer_subscriptions?select=manufacturer_id,tier`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        );
        if (sRes.ok) {
          const subs: { manufacturer_id: string; tier: string }[] = await sRes.json();
          setSubMap(new Map(subs.map((s) => [s.manufacturer_id, s.tier])));
        }
      } catch { /* subscriptions table may not exist yet — silently skip */ }

      // Fetch user counts per manufacturer
      try {
        const uRes = await fetch(
          `${SB_URL}/rest/v1/manufacturer_users?select=manufacturer_id`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        );
        if (uRes.ok) {
          const rows: { manufacturer_id: string }[] = await uRes.json();
          const counts = new Map<string, number>();
          for (const r of rows) counts.set(r.manufacturer_id, (counts.get(r.manufacturer_id) ?? 0) + 1);
          setUserCountMap(counts);
        }
      } catch { /* manufacturer_users not accessible — skip */ }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { load(); }, [load]);

  /* ── Sort / filter ──────────────────────────────────────────── */
  const sorted = useMemo(() => {
    let list = manufacturers;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((m) => m.name.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      let diff = 0;
      if (sortKey === "name") diff = a.name.localeCompare(b.name);
      else if (sortKey === "product_count") diff = (a.product_count ?? 0) - (b.product_count ?? 0);
      else diff = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return sortDir === "asc" ? diff : -diff;
    });
  }, [manufacturers, search, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k ? <span className="ml-1 text-orange-400">{sortDir === "asc" ? "↑" : "↓"}</span> : null;

  /* ── Add ────────────────────────────────────────────────────── */
  const handleAdd = async (form: FormState) => {
    if (!jwt) return;
    setSaving(true);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/manufacturers`, {
        method: "POST",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          contact_name: form.contact_name.trim() || null,
          contact_email: form.contact_email.trim() || null,
          website: form.website.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "Manufacturer added" });
      setShowAdd(false);
      await load();
    } catch (e) {
      toast({ title: "Failed to add", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  /* ── Edit ───────────────────────────────────────────────────── */
  const handleEdit = async (id: string, form: FormState) => {
    if (!jwt) return;
    setSaving(true);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/manufacturers?id=eq.${id}`, {
        method: "PATCH",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          contact_name: form.contact_name.trim() || null,
          contact_email: form.contact_email.trim() || null,
          website: form.website.trim() || null,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "Saved" });
      setEditingId(null);
      await load();
    } catch (e) {
      toast({ title: "Save failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ─────────────────────────────────────────────────── */
  const handleDelete = async (id: string) => {
    const m = manufacturers.find((x) => x.id === id);
    if ((m?.product_count ?? 0) > 0) {
      toast({ title: "Cannot delete", description: "Remove all products first.", variant: "destructive" });
      setConfirmDeleteId(null);
      return;
    }
    if (!jwt) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/manufacturers?id=eq.${id}`, {
        method: "DELETE",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "Deleted" });
      setConfirmDeleteId(null);
      await load();
    } catch (e) {
      toast({ title: "Delete failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Request Assets ─────────────────────────────────────────── */
  const ASSET_REQUEST_COOLDOWN_DAYS = 7;

  function assetRequestCoolingDown(m: Manufacturer): boolean {
    if (!m.last_asset_request_sent_at) return false;
    const sent = new Date(m.last_asset_request_sent_at).getTime();
    const diffDays = (Date.now() - sent) / (1000 * 60 * 60 * 24);
    return diffDays < ASSET_REQUEST_COOLDOWN_DAYS;
  }

  const handleRequestAssets = async (m: Manufacturer) => {
    if (!jwt || requestingAssets.has(m.id)) return;
    if (!m.contact_email) {
      toast({ title: "No contact email", description: "Add a contact email for this manufacturer first.", variant: "destructive" });
      return;
    }
    setRequestingAssets((prev) => new Set(prev).add(m.id));
    try {
      const res = await fetch(`${SB_URL}/functions/v1/send-asset-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ manufacturer_id: m.id }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `${res.status}`);
      }
      toast({ title: "Asset request sent", description: `Email sent to ${m.contact_email}` });
      await load();
    } catch (e) {
      toast({ title: "Failed to send", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setRequestingAssets((prev) => { const next = new Set(prev); next.delete(m.id); return next; });
    }
  };

  /* ── Duplicate detection ─────────────────────────────────────── */
  const duplicateNames = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of manufacturers) counts[m.name] = (counts[m.name] ?? 0) + 1;
    return new Set(Object.entries(counts).filter(([, n]) => n > 1).map(([name]) => name));
  }, [manufacturers]);

  /* ── Status helpers ──────────────────────────────────────────── */
  function mfrStatus(id: string): SubTier {
    const tier = subMap.get(id);
    if (tier === "premium") return "premium";
    if (tier === "paid") return "paid";
    if (tier === "free") return "free";
    return "pending";
  }

  /* ── Invite modal ────────────────────────────────────────────── */
  const openInviteModal = (m: Manufacturer) => {
    setInviteModalMfr(m);
    setInviteEmail("");
    setInviteRole("viewer");
    setInviteError(null);
  };

  const handleInviteSubmit = async () => {
    if (!jwt || !inviteModalMfr) return;
    const email = inviteEmail.trim();
    if (!email) { setInviteError("Email is required."); return; }
    setInviteLoading(true);
    setInviteError(null);
    try {
      const res = await fetch(`${SB_URL}/functions/v1/invite-manufacturer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ email, manufacturer_id: inviteModalMfr.id, role: inviteRole }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `${res.status}`);
      }
      setInviteModalMfr(null);
      toast({ title: "Invite sent", description: `Invitation sent to ${email}` });
      await load();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : "Failed to send invite.");
    } finally {
      setInviteLoading(false);
    }
  };

  /* ── Remove user ─────────────────────────────────────────────── */
  const handleRemoveUser = async (manufacturerId: string, userId: string) => {
    if (!jwt) return;
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/manufacturer_users?manufacturer_id=eq.${manufacturerId}&user_id=eq.${userId}`,
        { method: "DELETE", headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "User removed" });
      await load();
    } catch (e) {
      toast({ title: "Remove failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    }
  };

  /* ── Reset Demo ─────────────────────────────────────────────── */
  const handleResetDemo = async (id: string) => {
    if (!jwt || resettingDemoId === id) return;
    setResettingDemoId(id);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/rpc/reset_demo_data`, {
        method: "POST",
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ manufacturer_id: id }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "Demo data reset — re-run seed script to repopulate" });
    } catch (e) {
      toast({ title: "Reset failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setResettingDemoId(null);
    }
  };

  /* ── Stats ──────────────────────────────────────────────────── */
  const totalProducts = manufacturers.reduce((s, m) => s + (m.product_count ?? 0), 0);

  const editingMfr = manufacturers.find((m) => m.id === editingId);

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Manufacturers</h1>
        <button
          onClick={() => setShowAdd(true)}
          className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 transition"
        >
          + Add Manufacturer
        </button>
      </div>

      {/* Stats */}
      {!loading && (
        <div className="flex gap-4 text-sm">
          <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3">
            <p className="text-xs text-gray-500 mb-0.5">Manufacturers</p>
            <p className="text-xl font-semibold">{manufacturers.length}</p>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3">
            <p className="text-xs text-gray-500 mb-0.5">Total Products</p>
            <p className="text-xl font-semibold">{totalProducts}</p>
          </div>
        </div>
      )}

      {/* Duplicate warning banner */}
      {!loading && duplicateNames.size > 0 && (
        <div className="rounded-lg border border-yellow-800/60 bg-yellow-900/20 px-4 py-3 flex items-center gap-2 text-sm text-yellow-300">
          <span>⚠</span>
          <span>
            <strong>{duplicateNames.size}</strong> manufacturer name{duplicateNames.size !== 1 ? "s have" : " has"} duplicates. Review and merge before going live.
          </span>
        </div>
      )}

      {/* Search */}
      <input
        className="w-full max-w-xs rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 outline-none focus:border-orange-500 transition"
        placeholder="Search by company name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-900/40 bg-red-900/20 px-4 py-3 text-sm text-red-400">
          {error}
          <button onClick={load} className="ml-3 underline hover:text-red-300">Retry</button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl border border-gray-800 bg-gray-900 animate-pulse" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center space-y-2">
          <p className="text-sm text-gray-500">
            {search ? "No manufacturers match your search." : "No manufacturers yet."}
          </p>
          <button onClick={() => setShowAdd(true)} className="text-xs text-orange-400 hover:underline">
            Add your first manufacturer
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr className="border-b border-gray-800">
                <th className="px-4 py-3 text-left" />
                <th
                  className="px-4 py-3 text-left font-medium text-gray-400 cursor-pointer hover:text-gray-200"
                  onClick={() => toggleSort("name")}
                >
                  Company <SortIcon k="name" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 hidden md:table-cell">Contact</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 hidden lg:table-cell">Email</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400 hidden xl:table-cell">Website</th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-400 cursor-pointer hover:text-gray-200"
                  onClick={() => toggleSort("product_count")}
                >
                  Products <SortIcon k="product_count" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Status</th>
                <th
                  className="px-4 py-3 text-left font-medium text-gray-400 cursor-pointer hover:text-gray-200 hidden md:table-cell"
                  onClick={() => toggleSort("created_at")}
                >
                  Added <SortIcon k="created_at" />
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <>
                  <tr
                    key={m.id}
                    className={`border-t border-gray-800 hover:bg-gray-900/40 transition cursor-pointer ${
                      expandedId === m.id ? "bg-gray-900/60" : ""
                    }`}
                    onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
                  >
                    {/* Avatar + duplicate dot */}
                    <td className="px-4 py-3">
                      <div className="relative inline-block">
                        <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-semibold text-gray-300 select-none">
                          {initials(m.name)}
                        </div>
                        {duplicateNames.has(m.name) && (
                          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-yellow-400 border border-gray-950" title="Duplicate name" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-100">
                      <div className="flex items-center gap-2">
                        {m.name}
                        {(userCountMap.get(m.id) ?? 0) > 0 && (
                          <span className="inline-flex items-center rounded-full bg-gray-700 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">
                            {userCountMap.get(m.id)} user{(userCountMap.get(m.id) ?? 0) !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden md:table-cell">
                      {m.contact_name ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden lg:table-cell">
                      {m.contact_email ? (
                        <a href={`mailto:${m.contact_email}`} className="hover:text-orange-400 transition" onClick={(e) => e.stopPropagation()}>
                          {m.contact_email}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-400 hidden xl:table-cell">
                      {m.website ? (
                        <a href={m.website} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition truncate max-w-[140px] block" onClick={(e) => e.stopPropagation()}>
                          {m.website.replace(/^https?:\/\//, "")}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${(m.product_count ?? 0) > 0 ? "text-gray-200" : "text-gray-600"}`}>
                        {m.product_count ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {m.is_demo ? (
                        <span className="inline-flex items-center rounded-full border border-amber-700/60 bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-400">
                          Demo
                        </span>
                      ) : (
                        <MfrStatusBadge tier={mfrStatus(m.id)} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">
                      {new Date(m.created_at).toLocaleDateString(undefined, {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 justify-end flex-wrap">
                        {(() => {
                          const cooling = assetRequestCoolingDown(m);
                          const busy = requestingAssets.has(m.id);
                          return (
                            <button
                              onClick={() => handleRequestAssets(m)}
                              disabled={busy || cooling || !m.contact_email}
                              title={
                                !m.contact_email
                                  ? "Add a contact email first"
                                  : cooling
                                  ? `Request sent ${new Date(m.last_asset_request_sent_at!).toLocaleDateString()} — wait 7 days`
                                  : "Email asset request to manufacturer"
                              }
                              className="text-xs text-blue-400 hover:text-blue-300 transition disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              {busy ? "Sending…" : "Request Assets"}
                            </button>
                          );
                        })()}
                        <button
                          onClick={() => {
                            localStorage.setItem(IMPERSONATE_KEY, m.id);
                            window.dispatchEvent(new Event(IMPERSONATE_EVENT));
                            navigate("/manufacturer/overview");
                          }}
                          className="text-xs text-teal-400 hover:text-teal-300 transition"
                          title="View portal as this manufacturer"
                        >
                          Impersonate
                        </button>
                        {m.is_demo && (
                          <button
                            onClick={() => { void handleResetDemo(m.id); }}
                            disabled={resettingDemoId === m.id}
                            className="text-xs text-amber-400 hover:text-amber-300 transition disabled:opacity-40"
                            title="Reset demo data"
                          >
                            {resettingDemoId === m.id ? "Resetting…" : "Reset Demo"}
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/products?manufacturer=${m.id}`)}
                          className="text-xs text-gray-400 hover:text-orange-400 transition"
                          title="View Products"
                        >
                          Products
                        </button>
                        <button
                          onClick={() => openInviteModal(m)}
                          className="text-xs text-purple-400 hover:text-purple-300 transition"
                          title="Invite a user to this manufacturer portal"
                        >
                          Invite
                        </button>
                        <button
                          onClick={() => setEditingId(m.id)}
                          className="text-xs text-gray-400 hover:text-gray-200 transition"
                        >
                          Edit
                        </button>
                        {confirmDeleteId === m.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDelete(m.id)}
                              disabled={deletingId === m.id}
                              className="text-xs text-red-400 hover:text-red-300 transition disabled:opacity-50"
                            >
                              {deletingId === m.id ? "…" : "Confirm"}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="text-xs text-gray-500 hover:text-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(m.id)}
                            className="text-xs text-gray-600 hover:text-red-400 transition"
                            title={(m.product_count ?? 0) > 0 ? "Remove all products first" : "Delete"}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded: users + products */}
                  {expandedId === m.id && (
                    <tr key={`${m.id}-exp`} className="border-t border-gray-800/50 bg-gray-950/40">
                      <td colSpan={9} className="py-1">
                        <ManufacturerUsersList
                          manufacturerId={m.id}
                          jwt={jwt}
                          onRemove={(userId) => { void handleRemoveUser(m.id, userId); }}
                        />
                        <ManufacturerProducts
                          manufacturerId={m.id}
                          jwt={jwt}
                          navigate={navigate}
                        />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add drawer */}
      {showAdd && (
        <ManufacturerDrawer
          initial={BLANK_FORM}
          onSave={handleAdd}
          onClose={() => setShowAdd(false)}
          saving={saving}
        />
      )}

      {/* Edit drawer */}
      {editingId && editingMfr && (
        <ManufacturerDrawer
          initial={{
            name: editingMfr.name,
            contact_name: editingMfr.contact_name ?? "",
            contact_email: editingMfr.contact_email ?? "",
            website: editingMfr.website ?? "",
          }}
          onSave={(f) => handleEdit(editingId, f)}
          onClose={() => setEditingId(null)}
          saving={saving}
        />
      )}

      {/* Invite modal */}
      <Dialog open={!!inviteModalMfr} onOpenChange={(open) => { if (!open) setInviteModalMfr(null); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-gray-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-100">
              Invite user to {inviteModalMfr?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-300">Email address</label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-gray-950 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-orange-500"
                onKeyDown={(e) => { if (e.key === "Enter") { void handleInviteSubmit(); } }}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-300">Role</label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "viewer" | "admin")}>
                <SelectTrigger className="bg-gray-950 border-gray-700 text-gray-100 focus:ring-orange-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-gray-100">
                  <SelectItem value="viewer">Viewer — read-only access</SelectItem>
                  <SelectItem value="admin">Admin — full portal access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {inviteError && (
              <p className="text-sm text-red-400 rounded-md border border-red-900/40 bg-red-900/20 px-3 py-2">
                {inviteError}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <button
              onClick={() => setInviteModalMfr(null)}
              className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => { void handleInviteSubmit(); }}
              disabled={inviteLoading}
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
            >
              {inviteLoading ? "Sending…" : "Send Invite"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
